import '../../@types';
import type AccountAPI from '@intlsdk/account-api';
import type { LoginResp, ThirdAuthorizeParams, ThirdChannelName } from '@intlsdk/account-api';
import type { PassFactory } from '../../@types/lipass';
import Cookies from 'js-cookie';
import { loadScript, loadStyle, loadStyleByUrl, waitLoading } from './loader';
import { isInWujie, isProductEnv } from './env';
import { isINTLBrowser, isMobile, json_decode } from './tools';
import { emitEvent } from './event';

// - 登录配置

const config = {
    env: isProductEnv() ? 'hok' : 'test',
    gameID: 29134, // Player Network 游戏唯一标识 ID
    appID: '304953b95531249e24a5dcb5104e5ed7', //Player Network 中的 ACCOUNT_APP_ID
    accountPlatType: 131, // 账号平台类型，填写 Player Network 中的 ACCOUNT_PLAT_TYPE。LI PASS 填写 131
    hostCAcc: isProductEnv() ? 'https://li-sg.intlgame.com' : 'https://test-web-pass.intlgame.com',
};

export function getConfig(): typeof config {
    return config;
}

// - 使用 LEVEL INFINITE PASS 组件登录

/**
 * 加载 LI PASS 脚本
 * @see https://docs.playernetwork.intlgame.com/docs/zh/Level-Infinite-Pass/Integration/Get-started#%E4%BD%BF%E7%94%A8%E7%BD%91%E9%A1%B5%E7%99%BB%E5%BD%95%E7%BB%84%E4%BB%B6%E5%AE%8C%E6%88%90%E7%99%BB%E5%BD%95%E6%B5%81%E7%A8%8B
 * */
export async function loadLIPASS(): Promise<typeof PassFactory> {
    // @ts-ignore
    const ret = await waitLoading('PassFactory', () => window.PassFactory);
    if (ret) return ret;
    if (isProductEnv()) {
        loadStyleByUrl('https://common-web.intlgame.com/sdk-cdn/infinite-pass/latest/index.css');
        await loadScript('https://common-web.intlgame.com/sdk-cdn/infinite-pass/latest/index.umd.js', undefined, { id: 'infinite-pass' });
    } else {
        loadStyleByUrl('https://test-common-web.intlgame.com/sdk-cdn/infinite-pass/latest/index.css');
        await loadScript('https://test-common-web.intlgame.com/sdk-cdn/infinite-pass/latest/index.umd.js', undefined, { id: 'infinite-pass' });
    }
    // @ts-ignore
    return window.PassFactory;
}

export declare type SupportedSocial = 'facebook' | 'twitter' | 'google' | 'apple' | 'line' | 'steam' | 'epic' | 'ps5' | 'discord' | 'vk' | 'twitch';

let liPaas: PassFactory.Pass;

/**
 * 使用 LI PASS 登录组件
 * @param socialList 使用的社媒账号列表，默认 ['facebook', 'line']
 * @param onLoginSuccess 登录成功回调，参数为用户信息
 * @see https://docs.playernetwork.intlgame.com/docs/zh/Level-Infinite-Pass/Integration/Get-started#start
 */
export async function useLIPASS(
    langType: PassFactory.LangType,
    socialList: SupportedSocial[] = ['facebook', 'line'],
    onLoginSuccess?: (userInfo: PassFactory.UserInfo) => any,
    configs?: PassFactory.PassOptions['config'],
) {
    const id = 'infinite-pass-component';
    if (!liPaas) {
        const PassFactory = await loadLIPASS();
        const options = {
            env: config.env, // sdk 运行的环境
            gameID: config.gameID, // Player Network 中配置的 Game ID
            appID: config.appID, // Player Network 中配置的 APP_ID
            config: {
                isMobile: isMobile(),
                socialList,
                language_data: langType,
            },
        };
        if (configs) Object.assign(options.config, configs);
        console.debug('[Act][useLIPASS] options =', options);
        liPaas = new PassFactory.Pass(options);
        liPaas.on('onLoginError', (err: any) => {
            console.error('[Act][useLIPASS][onLoginError] 登录失败:', err);
        });
        // 登录组件挂载节点
        const div = document.createElement('div') as HTMLDivElement;
        div.id = id;
        const wrap = document.createElement('div') as HTMLDivElement;
        wrap.id = div.id + '-wrap';
        wrap.appendChild(div);
        loadStyle(`#${wrap.id} {
position: fixed;
left: 0;
top: 0;
width: 100%;
height: 100%;
background-color: rgba(0, 0, 0, 0.25);
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
z-index: 9999;
}`);
        document.body.appendChild(wrap);
    }

    return liPaas.start('#' + id).then((userInfo: PassFactory.UserInfo) => {
        if (userInfo.ret != 0) {
            console.error('[Act][useLIPASS] 登录失败:', userInfo);
            throw userInfo;
        }
        console.info('[Act][useLIPASS] 登录成功:', userInfo);
        liPaas.unmount();
        document.getElementById(id + '-wrap')!.style.display = 'none';
        Cookies.set('milo_intl_cookie', JSON.stringify(userInfo), { expires: new Date(userInfo.token_expire_time * 1000) });
        onLoginSuccess?.(userInfo);
        // 触发登录成功事件
        emitEvent('login', userInfo);
        return userInfo;
    });
}

// - 使用第三方账号登录

export async function loadAccountAPI(): Promise<typeof AccountAPI> {
    // @ts-ignore
    const ret = await waitLoading('IntlgameAccountApi', () => window.IntlgameAccountApi);
    if (ret) return ret;
    if (isProductEnv()) await loadScript('https://common-web.intlgame.com/sdk-cdn/account-api/latest/index.umd.js');
    else await loadScript('https://test-common-web.intlgame.com/sdk-cdn/account-api/latest/index.umd.js');
    // @ts-ignore
    return window.IntlgameAccountApi;
}

let accountAPI: AccountAPI;

const channelIDMap: { [k in ThirdChannelName]: number } = {
    wechat: 1,
    qq: 2,
    // guest: 3,
    facebook: 4,
    fbInstantGame: 4,
    // gamecenter: 5,
    google: 6,
    twitter: 9,
    garena: 10,
    // switch: 13,
    line: 14,
    apple: 15,
    vk: 19,
    xbox: 20,
    steam: 21,
    epic: 24,
    discord: 26,
    ps5: 27,
    twitch: 28,
    // dmm: 30,
    // squareenix: 32,
    kakaov3: 35,
    // ubisoft: 37,
    // vng: 38,
    // lipass: 131,
};

export async function getAccountAPI(langType: PassFactory.LangType = 'en') {
    if (!accountAPI) {
        const API = await loadAccountAPI();
        const api = new API({
            env: config.env,
            gameID: config.gameID,
            appID: config.appID,
            accountPlatType: config.accountPlatType,
            hostCAcc: config.hostCAcc,
            langType,
        });
        accountAPI = api;
        // @ts-ignore
        window.accountApi = accountAPI;
    }
    return accountAPI;
}

/**
 * 第三方渠道登录
 * @param langType 语言
 * @param channelName 渠道
 * @param extra 渠道参数
 * @param onLoginSuccess 登录成功回调
 * @see https://docs.playernetwork.intlgame.com/docs/zh/API/Web/Account/ThirdAuthorize/
 */
export async function useThirdLogin(langType: PassFactory.LangType, channelName: ThirdChannelName, extra?: ThirdAuthorizeParams['extra'], onLoginSuccess?: (userInfo: LoginResp) => any) {
    await getAccountAPI(langType);
    const options = {
        third_type: channelName,
        extra: {},
    };
    if (extra) Object.assign(options.extra, extra);
    console.debug('[Act][useThirdLogin] options =', options);
    return accountAPI.login(options).then((rsp) => {
        if (rsp.ret != 0) {
            console.error('[Act][useThirdLogin] 第三方登录失败:', rsp);
            throw rsp;
        }
        // @ts-ignore
        rsp.channel_info.openid = rsp.uid;
        rsp.channel_info.channelId = channelIDMap[channelName];
        console.debug('[Act][useThirdLogin] 第三方登录成功:', rsp);
        // 写 Cookie
        Cookies.set('milo_intl_cookie', JSON.stringify(rsp), { expires: new Date(rsp.token_expire_time * 1000) });
        onLoginSuccess?.(rsp);
        // 触发登录成功事件
        emitEvent('login', rsp);
        return rsp;
    });
}

export async function getBindChannels(openid: string, token: string, channel_id: number) {
    await getAccountAPI();
    // @ts-ignore
    const channels = await accountAPI.getBindChannelsByOpenID({ openid, token, channel_id });
    console.info('[Act][getBindChannels] 获取绑定列表:', channels);
    return channels;
}

export function getChannelIdByName(channelName: ThirdChannelName) {
    return channelIDMap[channelName];
}

/** 获取 WUJIE 的登录用户信息 */
export async function getWujieUserInfo(): Promise<WUJIE.UserInfo | null> {
    try {
        if (!isInWujie()) return null;
        let user: WUJIE.UserInfo | null = null;
        // WUJIE 真 TMD 坑，同步接口返回的 user 里竟然丢了 token，难怪一丢的奇葩问题。。。。
        do {
            if ($wujie.props.getAsyncUserAuthInfo)
                user = await $wujie.props.getAsyncUserAuthInfo({
                    auth: true,
                    ignoreUpdateToken: false,
                });
            else user = $wujie.props.getUserAuthInfo();
            if (!user) break;
            console.debug('[Act][getWujieUserInfo] 异步从接口获取到用户信息:', user);
            if (!user.role?.token) {
                console.warn('[Act][getWujieUserInfo] token 丢失……', user);
                user = null;
                break;
            }
            return user;
        } while (false);

        let str: string | null;
        // 从 session storage 获取用户信息
        do {
            if (user) break;
            str = parent.window.sessionStorage.getItem('CAMP_HOK_USER_INFO');
            if (!str) break;
            console.debug('[Act][getWujieUserInfo] 从 sessionStorage 获取用户信息:', str);
            str = atob(str);
            if (!str) break;
            str = decodeURIComponent(str);
            user = json_decode(str);
            if (!user) break;
            if (!user.role?.token) {
                console.warn('[Act][getWujieUserInfo] token 丢失……', user);
                user = null;
                break;
            }
            return user;
        } while (false);

        // 从 local storage 获取用户信息
        do {
            if (user) break;
            str = parent.window.localStorage.getItem('CAMP_HOK_USER_INFO');
            if (!str) break;
            console.debug('[Act][getWujieUserInfo] 从 localStorage 获取用户信息:', str);
            str = atob(str);
            if (!str) break;
            str = decodeURIComponent(str);
            user = json_decode(str);
            if (!user) break;
            if (!user.role?.token) {
                console.warn('[Act][getWujieUserInfo] token 丢失……', user);
                user = null;
                break;
            }
            return user;
        } while (false);

        return user;
    } catch (err) {
        console.error('[Act][getWujieUserInfo] 用户信息获取异常', err);
    }
    return null;
}

// 综合站再游戏内处理时需要再监听一下 auth_change 事件
if (isInWujie() && isINTLBrowser()) {
    console.debug('[Act][WUJIE] INTL 环境中监听 auth_change 处理登录态变化');
    $wujie.bus.$on('auth_change', (campUser) => {
        console.info('[Act][WUJIE] on auth_change:', campUser);
        if (!campUser?.role) return;
        const userInfo = {
            ret: 0,
            msg: 'ok',
            uid: campUser.role.uid!,
            openid: campUser.role.openId!,
            partition: campUser.role.partition, // WUJIE 登录中有 partition 信息
            token: campUser.role.token!,
            token_expire_time: Math.floor(Date.now() / 1000) + 3600,
            user_name: campUser.role.characName!,
            picture_url: campUser.role.headUrl,
            birthday: '',
            del_account_status: -1,
            channel_info: {
                channelId: campUser.role.channel as number,
                openid: campUser.role.openId!,
                access_token: campUser.role.channelAccessToken!,
                token: campUser.role.token,
                picture: campUser.role.headUrl,
            },
            seq: '',
        };
        Cookies.set('milo_intl_cookie', JSON.stringify(userInfo), { expires: new Date(userInfo.token_expire_time * 1000) });
        console.debug('[Act][initLoginStatus] userinfo =', userInfo);
        // 触发登录成功事件
        emitEvent('login', userInfo);
    });
}

// - 在社区综合站 WUJIE 框架里登录

// 已在综合站登录时，直接构造登录态返回
async function initLoginStatus() {
    let userInfo: PassFactory.UserInfo | null = null;
    if (isInWujie()) {
        // 需要同步登录态，未登录则清楚之前的登录态
        const campUser = await getWujieUserInfo();
        if (!campUser?.role) {
            Cookies.remove('milo_intl_cookie');
            return;
        }
        userInfo = {
            ret: 0,
            msg: 'ok',
            uid: campUser.role.uid!,
            openid: campUser.role.openId!,
            partition: campUser.role.partition, // WUJIE 登录中有 partition 信息
            token: campUser.role.token!,
            token_expire_time: Math.floor(Date.now() / 1000) + 3600,
            user_name: campUser.role.characName!,
            picture_url: campUser.role.headUrl,
            birthday: '',
            del_account_status: -1,
            channel_info: {
                channelId: campUser.role.channel as number,
                openid: campUser.role.openId!,
                access_token: campUser.role.channelAccessToken!,
                token: campUser.role.token,
                picture: campUser.role.headUrl,
            },
            seq: '',
        };
        Cookies.set('milo_intl_cookie', JSON.stringify(userInfo), { expires: new Date(userInfo.token_expire_time * 1000) });
        console.debug('[Act][initLoginStatus] userinfo =', userInfo);
    } else {
        const str = Cookies.get('milo_intl_cookie');
        if (!str) return;
        userInfo = json_decode(str) as PassFactory.UserInfo;
    }

    if (userInfo && userInfo.ret == 0 && userInfo.openid) {
        // 页面加载后再触发登录成功事件
        emitEvent('login', userInfo);
    }
}

initLoginStatus();

/** 拉起综合站登录面板进行登录 */
export async function useWujieLogin(onLoginSuccess?: (userInfo: LoginResp) => any): Promise<LoginResp> {
    if (!isInWujie()) {
        const err = {
            ret: -1,
            msg: 'not in wujie',
        } as any;
        console.error('[Act][useWujieLogin] 当前不在 WUJIE 上下文', err);
        return err;
    }

    return new Promise((resolve, reject) => {
        const loginapi = $wujie.props.login({
            onSuccess(campUser) {
                console.info('[Act][useWujieLogin] 登录成功:', campUser);
                loginapi?.close();
                const userinfo: PassFactory.UserInfo = {
                    ret: 0,
                    msg: 'ok',
                    uid: campUser.role.uid,
                    openid: campUser.role.openId,
                    partition: campUser.role.partition, // WUJIE 登录中有 partition 信息
                    token: campUser.role.token,
                    token_expire_time: Math.floor(Date.now() / 1000) + 3600,
                    user_name: campUser.role.characName,
                    picture_url: campUser.role.headUrl,
                    birthday: '',
                    del_account_status: -1,
                    channel_info: {
                        channelId: campUser.role.channel,
                        openid: campUser.role.openId,
                        access_token: campUser.role.channelAccessToken,
                        token: campUser.role.token,
                        picture: campUser.role.headUrl,
                    },
                    seq: '',
                };
                Cookies.set('milo_intl_cookie', JSON.stringify(userinfo), { expires: new Date(userinfo.token_expire_time * 1000) });
                console.debug('[Act][useWujieLogin] userinfo =', userinfo);

                const rsp: LoginResp = {
                    ret: 0,
                    msg: 'ok',
                    access_token: campUser.role.channelAccessToken,
                    token: campUser.role.token,
                    openid: campUser.role.openId,
                    user_name: campUser.role.characName,
                    picture_url: campUser.role.headUrl,
                    email: '',
                    birthday: '',
                    gender: 0,
                    token_expire_time: userinfo.token_expire_time,
                    channel_info: userinfo.channel_info,
                    first_login: -1,
                    need_name_auth: false,
                    need_notify: false,
                    pf: '',
                    pf_key: '',
                    reg_channel_dis: '',
                    del_li_account_status: -1,
                    del_account_info: '',
                    del_account_status: -1,
                    extra_json: {},
                };
                onLoginSuccess?.(rsp);
                resolve(rsp);
            },
            onFail(errorCode) {
                console.error('[Act][useWujieLogin] 登录失败:', errorCode);
                loginapi?.close();
                reject(errorCode);
            },
        });
    });
}

export function getWujieObject(): typeof $wujie | null {
    if (!isInWujie()) return null;
    return $wujie;
}

export function checkLogin() {
    const str = Cookies.get('milo_intl_cookie');
    if (str == null) return false;
    const userinfo = json_decode(str) as PassFactory.UserInfo;
    if (userinfo == null) return false;
    const now = Math.ceil(new Date().getTime() / 1000);
    if (now >= userinfo.token_expire_time) {
        console.debug('[Act][checkLogin] 登录态已过期请重新登录');
        return false;
    }
    console.debug('[Act][checkLogin] 已登录', `${now} < ${userinfo.token_expire_time}`, userinfo);
    return userinfo;
}

/** 退出登录 */
export async function logout() {
    // 清楚WUJIE登录态
    // if (isInWujie()) {
    //     parent.window.sessionStorage.removeItem('CAMP_HOK_USER_INFO');
    //     parent.window.localStorage.removeItem('CAMP_HOK_USER_INFO');
    // }
    Cookies.remove('logined_account_cache_key');
    Cookies.remove('milo_intl_cookie'); // 清除用于Milo的星图登录态
    console.info('[Act] logout: 退出登录，清楚登录态信息');
    return new Promise((resolve) => {
        setTimeout(() => {
            emitEvent('logout'); // 触发登出时间
            resolve(void 0);
        }, 10);
    });
}

// - 其它函数

export async function getUserRegion(): Promise<string | undefined> {
    if (isInWujie()) return $wujie.props.getUserRegion();
    return '344'; // TODO 获取用户国家或地区信息
    // return '392'; // 日本
    // await getAccountAPI();
    // const res = await accountAPI?.queryMyRegion();
    // if (!res || res.ret != 0) console.error('[Act][getUsrRegion] 无法获取用户国家或地区信息', res);
    // if (!res?.region) return undefined;
    // return res.region;
}
